import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../utils/tpoGenQueryKeys';

const PAGE_SIZE = 50;

const fetchPlanningGroups = async (
    searchKey: string,
    myPlansOnly: boolean,
    filters: Record<string, any>,
    sortBy: string,
    sortOrder: string,
    offset: number,
    signal?: AbortSignal,
) => {
    const currentUser = localStorage.getItem('email');
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.getPlanningGroups, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        data: {
            user_id: currentUser,
            sort_by: sortBy,
            sorting_order: sortOrder,
            limit: PAGE_SIZE,
            offset,
            search_keyword: searchKey,
            filters: filters,
            my_plans_only: myPlansOnly,
        },
        signal: signal,
    });
    return response.data;
};

const usePlanningGroupData = ({
    searchKey,
    myPlansOnly,
    filters,
    sortBy,
    sortOrder,
}: {
    searchKey: string;
    myPlansOnly: boolean;
    filters: Record<string, any>;
    sortBy: string;
    sortOrder: string;
}) => {
    const { data, isPending, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: tpoGenKeys.planningGroups(searchKey, myPlansOnly, filters, sortBy, sortOrder),
        queryFn: ({ signal, pageParam }) =>
            fetchPlanningGroups(searchKey, myPlansOnly, filters, sortBy, sortOrder, pageParam, signal),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const groups = lastPage?.planning_groups ?? [];
            return groups.length === PAGE_SIZE ? lastPageParam + PAGE_SIZE : undefined;
        },
    });

    const planningGroups = useMemo(() => data?.pages.flatMap((page: any) => page?.planning_groups ?? []) ?? [], [data]);

    return { data: planningGroups, isPending, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage };
};

export default usePlanningGroupData;
